import React from "react";

import { Add, Close, Save, Visibility } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import { makeStyles } from "tss-react/mui";

import useDialogState from "@/hooks/useDialogState";
import { colors } from "@/theme/overrides/colors";

import ComponentsCard from "./ComponentsCard";
import SectionsAndElements from "./SectionsAndElements";
import Form from "./store/Form";
import PreviewForm from "../PreviewForm";

interface Props {
  form: Form;
  onCancel?: () => void;
  onSave: () => Promise<void>;
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    margin: 14,
    height: "100%",
  },
  navBar: {
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
  },
  subNav: {
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "row",
  },
  componentsContainer: {
    backgroundColor: colors.white,
    height: "100%",
    width: "30%",
    marginRight: 14,
  },
  formContainer: {
    backgroundColor: colors.white,
    width: "70%",
    height: "100%",
  },
  grid: {
    flexGrow: 1,
    margin: theme.spacing(4, 2, 2, 2),
    backgroundColor: "#333",
  },
  formRoot: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    height: "calc(100vh - 190px)",
    marginTop: 8,
  },
  button: {
    marginLeft: 10,
    alignSelf: "center",
    borderColor: colors.grayLight,
  },
  saveModal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingBottom: 12,
  },
  icon: {
    color: colors.white,
    fontSize: "30px",
  },
  iconContainer: {
    height: "46px",
    width: "46px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    marginBottom: "20px",
    backgroundColor: "#26C9A7",
    marginTop: 12,
  },
}));

const FormBuilder: React.FC<Props> = observer(({ form, onCancel, onSave }) => {
  const { classes } = useStyles();
  const titleDialog = useDialogState();
  const previewDialog = useDialogState();

  async function onSubmit() {
    try {
      await onSave();
    } finally {
      titleDialog.hide();
    }
  }

  const addSection = () => {
    form.addSection();
    // Newly added section is the last section, so we'll just scroll the
    // last section into view to scroll into the new section.
    requestAnimationFrame(() => {
      const sections = document.querySelectorAll("[data-form-section]");
      sections[sections.length - 1]?.lastElementChild?.scrollIntoView({
        behavior: "smooth",
      });
    });
  };

  return (
    <form noValidate>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        marginBottom={4}
      >
        <Box className={classes.navBar}>
          <IconButton
            onClick={onCancel}
            size="large"
            sx={{ border: "1px solid #D4DBE5" }}
          >
            <ArrowBackIcon />
          </IconButton>

          <Typography variant="h6" style={{ marginLeft: 24 }}>
            {form.title || "Create form"}
          </Typography>
        </Box>

        <Box className={classes.subNav}>
          <div>
            <Button
              className={classes.button}
              variant="outlined"
              onClick={addSection}
              startIcon={<Add />}
            >
              Add section
            </Button>

            <Button
              className={classes.button}
              variant="outlined"
              onClick={previewDialog.show}
              startIcon={<Visibility />}
            >
              Preview
            </Button>
            <Button
              variant="outlined"
              className={classes.button}
              onClick={titleDialog.show}
              startIcon={<Save />}
            >
              Save
            </Button>
            <Button
              className={classes.button}
              variant="outlined"
              onClick={onCancel}
              startIcon={<Close />}
              color="inherit"
            >
              Cancel
            </Button>
          </div>
        </Box>
      </Box>

      <Divider sx={{ marginBottom: 6, color: "#DEE6F0" }} />

      <div className={classes.formRoot}>
        <ComponentsCard />
        <SectionsAndElements form={form} />
      </div>

      <Modal
        className={classes.saveModal}
        open={titleDialog.shown}
        onClose={titleDialog.hide}
      >
        <Box
          display="flex"
          flexDirection="column"
          style={{
            backgroundColor: colors.white,
            height: "auto",
            width: 500,
            paddingTop: 8,
            paddingRight: 12,
            paddingLeft: 12,
          }}
        >
          <Typography variant="h5" color="textPrimary">
            Save Form
          </Typography>

          <Typography
            variant="body2"
            color="textSecondary"
            style={{ alignSelf: "flex-start", marginBottom: 8 }}
          >
            Form title
          </Typography>
          <TextField
            fullWidth
            value={form.title}
            onChange={(e) => (form.title = e.target.value)}
          />
          <Box
            display="flex"
            flexDirection="row"
            marginTop={8}
            marginBottom={8}
            justifyContent="center"
          >
            <Button variant="outlined" onClick={titleDialog.hide}>
              Cancel
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={onSubmit}>
              Save
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* TODO: Implement new section preview */}
      <PreviewForm
        form={form}
        open={previewDialog.shown}
        onClose={previewDialog.hide}
      />
    </form>
  );
});

export default FormBuilder;
